import { FormikContextType } from "formik";
import { SoortFoto, WoningState, FotoWoningType } from "../infra/woning-schema";
import { uploadFotoVanWoningApi, getBlobUrlApi } from "../woning-api/woning-api";

/**
 * Calculate square mask based on image dimensions
 */
export const calculateSquareMask = (img: HTMLImageElement): string => {
  const { width: displayWidth, height: displayHeight } = img.getBoundingClientRect();

  // Use display dimensions for calculation
  const containerWidth = displayWidth;
  const containerHeight = displayHeight;

  const squareSizePixels = Math.min(containerWidth, containerHeight) * 0.9; // 80% of smaller dimension

  // Calculate percentages - BOTH width and height use the same pixel size
  const widthPercent = (squareSizePixels / containerWidth) * 100;
  const heightPercent = (squareSizePixels / containerHeight) * 100;

  // Calculate the offsets to center the square
  const leftPercent = (100 - widthPercent) / 2;
  const topPercent = (100 - heightPercent) / 2;
  const rightPercent = leftPercent + widthPercent;
  const bottomPercent = topPercent + heightPercent;

  // Create clip-path for the mask (everything except the center square)
  const clipPath = `polygon(0% 0%, 0% 100%, ${leftPercent}% 100%, ${leftPercent}% ${topPercent}%, ${rightPercent}% ${topPercent}%, ${rightPercent}% ${bottomPercent}%, ${leftPercent}% ${bottomPercent}%, ${leftPercent}% 100%, 100% 100%, 100% 0%)`;

  return clipPath;
};

export interface PerformStreetViewUploadParams {
  streetViewUrl: string;
  settings: any;
  user: any;
  vestiging: string;
  adviesdossier: string;
  modelPrefix: string;
  fotoWoning: FotoWoningType;
  formik: FormikContextType<WoningState>;
  setIsUploadingStreetView: (uploading: boolean) => void;
  setImageUpdateKey: (updater: (prev: number) => number) => void;
}

/**
 * Function that actually performs the street view upload
 */
export const performStreetViewUpload = async (params: PerformStreetViewUploadParams): Promise<void> => {
  const {
    streetViewUrl,
    settings,
    user,
    vestiging,
    adviesdossier,
    modelPrefix,
    fotoWoning,
    formik,
    setIsUploadingStreetView,
    setImageUpdateKey
  } = params;

  const model = `${modelPrefix}.fotoWoning`;
  setIsUploadingStreetView(true);

  try {
    // Convert the street view URL to a full HTTPS URL for fetching
    const fullUrl = streetViewUrl.startsWith("//") ? `https:${streetViewUrl}` : streetViewUrl;

    // Fetch the image as a blob
    const response = await fetch(fullUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    const imageBlob = await response.blob();

    // Convert blob to File object for upload
    const streetViewFile = new File([imageBlob], "streetview.jpg", {
      type: "image/jpeg",
      lastModified: Date.now()
    });

    // Upload the street view image
    const formData = new FormData();
    formData.append("fotoWoningBestand", streetViewFile);

    const success = await uploadFotoVanWoningApi(settings, user, vestiging, adviesdossier, formData, formik);

    if (!success) {
      throw new Error("Upload API returned false - backend call failed");
    }

    // Get the new blob URL
    const newLocatieFoto = await getBlobUrlApi(settings, user, vestiging, adviesdossier);

    if (!newLocatieFoto) {
      throw new Error("No new image URL received from backend");
    }

    const oldValue = fotoWoning.locatieFoto;

    // Add cache-busting parameter if URL is the same
    let finalUrl = newLocatieFoto;
    if (newLocatieFoto && newLocatieFoto === oldValue) {
      const separator = newLocatieFoto.includes("?") ? "&" : "?";
      finalUrl = `${newLocatieFoto}${separator}t=${Date.now()}`;
    }

    formik.setFieldValue(`${model}.locatieFoto`, finalUrl);

    // Set soortFoto to 'Eigen' since we uploaded a snapshot
    formik.setFieldValue(`${model}.soortFoto`, SoortFoto.Eigen);

    // Store the current Street View URL for future use
    formik.setFieldValue(`${model}.streetViewUrl`, streetViewUrl.replace("https://", ""));

    // Trigger a re-render to ensure UI updates
    setImageUpdateKey(prev => prev + 1);
  } catch (error) {
    formik.setFieldValue(`${model}.locatieFoto`, streetViewUrl.replace("https://", ""));

    // Store the current Street View URL for future use even in fallback case
    formik.setFieldValue(`${model}.streetViewUrl`, streetViewUrl.replace("https://", ""));
  } finally {
    setIsUploadingStreetView(false);
  }
};
